"use client";

import { useRef, useState } from "react";
import { useConfigurator } from "@/store/configurator";
import { noteNamesFor } from "@/lib/configurator/string-notes";
import { gaugeOptionsFor } from "@/lib/configurator/gauge-options";
import { formatGauge, isWound } from "@/lib/catalog/gauge-format";
import { configuratorFinish } from "@/lib/configurator/finish";
import { chipStyle } from "@/components/configurator/chip-style";
import type { WindingType } from "@/types/configurator";

// Windings offered per instrument category. Guitar family: Plain + Wound.
// Bass: Wound only. Ukulele: Plain only (nylon).
const WINDINGS: { value: WindingType; label: string; categories: string[] }[] = [
  { value: "plain", label: "Plain", categories: ["electric", "acoustic", "classical", "ukulele"] },
  { value: "round_wound", label: "Wound", categories: ["electric", "acoustic", "classical", "bass"] },
];

// Per-instrument fretboard wood tones (SVG gradient stops — no image assets).
// ponytail: gradient wood; swap to texture PNGs only if the look demands it.
const WOOD: Record<string, { top: string; bottom: string; grain: string; edge: string }> = {
  electric:  { top: "#6E4A2B", bottom: "#4A2F19", grain: "#815838", edge: "#2E1D0F" },
  acoustic:  { top: "#8A5A2B", bottom: "#5C3A1C", grain: "#9C6A38", edge: "#3A2513" },
  bass:      { top: "#5A3A22", bottom: "#37220F", grain: "#6E4A2E", edge: "#241608" },
  classical: { top: "#CBA063", bottom: "#9A7440", edge: "#6E4E28", grain: "#D8B074" },
  ukulele:   { top: "#CBA063", bottom: "#9A7440", edge: "#6E4E28", grain: "#D8B074" },
};

const STEEL = "#C8CCD4"; // uncolored string default

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/** Map a gauge (inches) to a visible stroke width. Thicker gauge → thicker line. */
function strokeFor(gaugeValue: string): number {
  const n = parseFloat(gaugeValue) || 0.01;
  const t = Math.max(0, Math.min(1, (n - 0.008) / (0.13 - 0.008)));
  return 1.6 + t * 6;
}

interface FretboardProps {
  /** "preview" renders the same board read-only: no editor, no buttons. */
  mode: "gauge" | "color" | "preview";
  /**
   * Color mode is controlled by the parent (one shared palette in ColorStep):
   * the selected string index (null = none), and the tap callback.
   */
  activeIndex?: number | null;
  onSelectString?: (index: number) => void;
}

export function Fretboard({ mode, activeIndex, onSelectString }: FretboardProps) {
  const { state, referenceData, updateString } = useConfigurator();
  const svgRef = useRef<SVGSVGElement>(null);
  const [internalActive, setInternalActive] = useState(0);
  // Gauge mode manages its own selection; color mode is driven by the parent.
  const active = mode === "color" ? activeIndex ?? null : internalActive;

  const strings = state.strings;
  const n = strings.length;
  const instrument = referenceData.instruments.find((i) => i.slug === state.instrumentId);
  const category = instrument?.category ?? "electric";
  const line = referenceData.lines.find((l) => l.slug === state.lineId);
  // Palettes are per instrument — see ColorStep.
  const colors = (line?.colors ?? []).filter(
    (color) => color.instrumentSlug === state.instrumentId
  );
  const notes = noteNamesFor(state.instrumentId, state.stringCount ?? n);
  const windings = WINDINGS.filter((w) => w.categories.includes(category));
  const wood = WOOD[category] ?? WOOD.electric;
  const readOnly = mode === "preview";
  const finish = configuratorFinish(category, line?.slug);

  if (n === 0) return null;

  function resolvedHex(index: number): string {
    const str = strings[index];
    // 1. Explicit per-string override always wins.
    if (str.colorId) {
      return colors.find((c) => c.slug === str.colorId)?.hexCode ?? STEEL;
    }
    // 2. A real set-color pick (different from the default finish) paints all.
    if (state.colorId && state.colorId !== finish.setColorSlug) {
      return colors.find((c) => c.slug === state.colorId)?.hexCode ?? STEEL;
    }
    // 3. Default finish preview: plains vs wound (e.g. acoustic Ultra-Sheer =
    //    gold plains / phos-bronze wound). Falls back to steel otherwise.
    if (finish.setHex) {
      return isWound(str.windingType) ? finish.setHex : finish.plainHex ?? finish.setHex;
    }
    return STEEL;
  }

  // Geometry — CSS aspect-ratio matches the viewBox, so the SVG scales
  // without distortion and the HTML overlay rows still line up with the bands.
  const W = 460;
  const band = readOnly ? 26 : 36; // preview is a summary, keep it compact
  const H = n * band;
  const nutX = 54;
  const frets = 6;

  function pluck(index: number) {
    if (!svgRef.current || prefersReducedMotion()) return;
    const g = svgRef.current.querySelector<SVGGElement>(`#fb-str-${index}`);
    if (!g) return;
    g.style.animation = "none";
    void g.getBoundingClientRect();
    g.style.animation = "string-pluck 0.6s ease-out";
  }

  function select(index: number) {
    if (mode === "color") {
      onSelectString?.(index);
    } else {
      setInternalActive(index);
    }
    pluck(index);
  }

  const activeStr = active !== null ? strings[active] : null;
  const activeNote = active !== null ? notes[active] ?? `String ${active + 1}` : "";

  return (
    <div>
      <div className="relative rounded-md overflow-hidden border" style={{ borderColor: "#2A2A2A" }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          aria-hidden="true"
          style={{ display: "block", width: "100%", height: "auto", aspectRatio: `${W} / ${H}` }}
        >
          <defs>
            <linearGradient id="fb-wood" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={wood.top} />
              <stop offset="1" stopColor={wood.bottom} />
            </linearGradient>
            <filter id="fb-glow" x="-5%" y="-200%" width="110%" height="500%">
              <feGaussianBlur stdDeviation="2.4" />
            </filter>
          </defs>

          {/* Neck */}
          <rect x="0" y="0" width={W} height={H} fill="url(#fb-wood)" />
          {/* Wood grain */}
          {Array.from({ length: n * 2 }).map((_, i) => (
            <line
              key={`grain-${i}`}
              x1={nutX} y1={(i + 0.5) * (H / (n * 2))} x2={W} y2={(i + 0.5) * (H / (n * 2)) + (i % 2 ? 3 : -3)}
              stroke={wood.grain} strokeWidth="1" opacity="0.15"
            />
          ))}
          {/* Nut */}
          <rect x={nutX - 6} y="0" width="6" height={H} fill="#E8E2D2" opacity="0.85" />
          <rect x="0" y="0" width={nutX - 6} height={H} fill={wood.edge} opacity="0.5" />
          {/* Frets */}
          {Array.from({ length: frets }).map((_, i) => {
            const x = nutX + ((i + 1) / (frets + 1)) * (W - nutX);
            const dotFrets = [2, 4]; // 5th & 9th-ish inlays
            return (
              <g key={`fret-${i}`}>
                <line x1={x} y1="0" x2={x} y2={H} stroke="#D9D2C4" strokeWidth="1.5" opacity="0.5" />
                {dotFrets.includes(i) && (
                  <circle cx={(x + nutX + (i / (frets + 1)) * (W - nutX)) / 2} cy={H / 2} r="3.5" fill="#EDE6D6" opacity="0.55" />
                )}
              </g>
            );
          })}

          {/* Strings */}
          {strings.map((str, i) => {
            const y = (i + 0.5) * band;
            const sw = strokeFor(str.gaugeValue);
            const hex = resolvedHex(i);
            const wound = isWound(str.windingType);
            const isActive = !readOnly && i === active;
            // Color/gauge changes animate; a set-level color change cascades
            // string by string ("painting" the set top to bottom).
            const anim: React.CSSProperties = prefersReducedMotion()
              ? {}
              : { transition: "stroke 150ms ease, stroke-width 200ms ease", transitionDelay: `${i * 25}ms` };
            const overridden = mode === "color" && state.isPerStringColor && str.colorId !== null;
            return (
              <g id={`fb-str-${i}`} key={str.position}>
                {isActive && (
                  <line x1={nutX} y1={y} x2={W} y2={y} stroke={hex} strokeWidth={sw + 5} opacity="0.35" filter="url(#fb-glow)" style={anim} />
                )}
                {/* Core */}
                <line x1={nutX} y1={y} x2={W} y2={y} stroke={hex} strokeWidth={sw} strokeLinecap="round" style={anim} />
                {/* Wound texture: fine coil sheen — coarse dashes blow up
                    into dots once the viewBox stretches to page width */}
                {wound && (
                  <line
                    x1={nutX} y1={y} x2={W} y2={y}
                    stroke="rgba(0,0,0,0.28)" strokeWidth={sw} strokeLinecap="butt"
                    strokeDasharray="0.8 1.3"
                    style={anim}
                  />
                )}
                {/* Specular highlight */}
                <line x1={nutX} y1={y - sw * 0.3} x2={W} y2={y - sw * 0.3} stroke="rgba(255,255,255,0.28)" strokeWidth={Math.max(sw * 0.3, 0.5)} strokeLinecap="round" style={anim} />
                {/* Per-string override marker at the nut */}
                {overridden && (
                  <circle cx={nutX - 16} cy={y} r="2.5" fill="#E8C547" opacity="0.9" />
                )}
              </g>
            );
          })}
        </svg>

        {/* Overlay: one row per string band, aligned to the SVG bands.
            Focusable edit buttons in interactive modes; plain labels in preview. */}
        <div className="absolute inset-0 flex flex-col">
          {strings.map((str, i) => {
            const note = notes[i] ?? `String ${i + 1}`;
            const labels = (
              <>
                <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded" style={{ color: "#0A0A0A", backgroundColor: "rgba(237,230,214,0.82)" }}>
                  {note}
                </span>
                <span className="text-[11px] font-mono px-1.5 py-0.5 rounded" style={{ color: "#F5F5F5", backgroundColor: "rgba(0,0,0,0.45)" }}>
                  {formatGauge(str.gaugeValue)}
                  {isWound(str.windingType) ? "w" : ""}
                </span>
              </>
            );
            if (readOnly) {
              return (
                <div key={str.position} className="flex-1 flex items-center justify-between px-3">
                  {labels}
                </div>
              );
            }
            const label =
              mode === "gauge"
                ? `${note}, gauge ${formatGauge(str.gaugeValue)}${isWound(str.windingType) ? " wound" : " plain"} — edit`
                : `${note} — pick a color for this string`;
            return (
              <button
                key={str.position}
                type="button"
                onClick={() => select(i)}
                aria-label={label}
                aria-pressed={i === active}
                className="flex-1 flex items-center justify-between px-3 text-left focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#E8C547]"
                style={{ background: i === active ? "rgba(232,197,71,0.06)" : "transparent" }}
              >
                {labels}
              </button>
            );
          })}
        </div>
      </div>

      {/* Editor panel — gauge mode only. Color mode has one shared palette
          in ColorStep, so the board is purely the selection surface there. */}
      {mode === "gauge" && activeStr && active !== null && (
      <div className="mt-3 rounded-md border p-3" style={{ borderColor: "#2A2A2A", backgroundColor: "#141414" }}>
        <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#9A9A9A" }}>
          Editing <span style={{ color: "#E8C547" }}>{activeNote}</span>
          <span className="ml-2 font-mono normal-case tracking-normal" style={{ color: "#7A7A7A" }}>
            {formatGauge(activeStr.gaugeValue)}{isWound(activeStr.windingType) ? "w" : ""}
          </span>
        </p>

        <div className="space-y-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: "#8A8A8A" }}>Gauge (thickness)</p>
            <div className="flex flex-wrap gap-1.5">
              {gaugeOptionsFor(category, state.stringCount ?? n, active, activeStr.gaugeValue).map((g) => {
                const selected = parseFloat(g) === parseFloat(activeStr.gaugeValue);
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => { updateString(activeStr.position, { gaugeValue: g }); pluck(active); }}
                    aria-pressed={selected}
                    className="px-3 py-2 text-xs font-mono rounded-sm border transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                    style={chipStyle(selected)}
                  >
                    {formatGauge(g)}
                  </button>
                );
              })}
            </div>
          </div>
          {windings.length > 1 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: "#8A8A8A" }}>Winding</p>
              <div className="flex flex-wrap gap-1.5">
                {windings.map((w) => {
                  const selected = activeStr.windingType === w.value;
                  return (
                    <button
                      key={w.value}
                      type="button"
                      onClick={() => updateString(activeStr.position, { windingType: w.value })}
                      aria-pressed={selected}
                      className="px-3 py-2 text-xs rounded-sm border transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                      style={chipStyle(selected)}
                    >
                      {w.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
